<template>
  <div class="project-internal">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('preparation') }}</strong>
        </legend>

        <b-form-group
          v-if="isAdmin"
          horizontal
          :label="$t('client')"
          label-for="client"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('client')"
            v-model="project.client"
            id="employer"
            :options="clients"
            :allow-empty="false"
            :show-labels="false"
            :disabled="isClient"
            deselectLabel
            track-by="number"
            label="name"
          >
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('employer')"
          label-for="employer"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('employer')"
            v-model="project.employer"
            id="employer"
            :options="employers"
            :allow-empty="true"
            :show-labels="false"
            deselectLabel
            track-by="number"
            label="name"
          >
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('inCharge')"
          label-for="inCharge"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            class="multiselect-users"
            :placeholder="'Mitarbeiter ' + $t('select') + '/suchen'"
            v-model="project.inCharge"
            :options="filteredUsers"
            track-by="number"
            :internal-search="false"
            :show-labels="false"
            :allow-empty="true"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            v-on:search-change="onUserSearch"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.firstname }} {{ option.lastname }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ option.firstname }} {{ option.lastname }}
            </template>
            <template slot="noResult">
              <div>{{ $t('no_results') }}</div>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('customerNumber')"
          label-for="customerNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="project.customer.number"
            type="text"
            :placeholder="$t('customerNumber')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('employerProjectNumber')"
          label-for="employerProjectNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="project.employerProjectNumber"
            type="text"
            :placeholder="$t('employerProjectNumber')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('deliveryAt')"
          label-for="deliveryAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="deliveryAt"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2019:2025"
          >
          </Calendar>
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('projectTypeState')"
          label-for="projectTypeState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('projectTypeState')"
            v-model="project.projectTypeState"
            id="projectTypeState"
            :options="getEnumValues('ProjectTypeState')"
            :allow-empty="true"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="projectTypeStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="projectTypeStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('projectState')"
          label-for="projectState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('projectState')"
            v-model="project.projectState"
            id="projectState"
            :options="getEnumValues('ProjectState')"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="projectStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="projectStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('projectDescriptionState')"
          label-for="projectDescriptionState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('projectDescriptionState')"
            v-model="project.projectDescriptionState"
            id="projectDescriptionState"
            :options="getEnumValues('DocumentState')"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="documentStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="documentStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('mandateState')"
          label-for="mandateState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          :invalid-feedback="invalidFeedbackNA"
          :state="requirementsValidState('mandateState')"
        >
          <multiselect
            :placeholder="$t('mandateState')"
            v-model="project.mandateState"
            id="mandateState"
            :options="getEnumValues('DocumentState')"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="documentStateColor(option)">
                {{ $t(option) }}
              </b-badge></template
            >
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="documentStateColor(option)">
                {{ $t(option) }}
              </b-badge></template
            >
          </multiselect>
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('layoutPlanState')"
          label-for="layoutPlanState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          :invalid-feedback="invalidFeedbackNA"
          :state="requirementsValidState('layoutPlanState')"
        >
          <multiselect
            :placeholder="$t('layoutPlanState')"
            v-model="project.layoutPlanState"
            id="layoutPlanState"
            :options="getEnumValues('DocumentState')"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="documentStateColor(option)">
                {{ $t(option) }}
              </b-badge></template
            >
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="documentStateColor(option)">
                {{ $t(option) }}
              </b-badge></template
            >
          </multiselect>
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('installationImagesState')"
          label-for="installationImagesState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('installationImagesState')"
            v-model="project.installationImagesState"
            id="installationImagesState"
            :options="getEnumValues('DocumentState')"
            :allow-empty="false"
            :show-labels="false"
            deselectLabel
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="documentStateColor(option)">
                {{ $t(option) }}
              </b-badge></template
            >
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="documentStateColor(option)">
                {{ $t(option) }}
              </b-badge></template
            >
          </multiselect>
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <div v-if="project.deferredState">
          <legend>
            <strong>{{ $t('deferredState') }}</strong>
          </legend>

          <div class="form-grid-container-left">
            <div class="label">{{ $t('deferredState') }}</div>
            <div>
              <multiselect
                :placeholder="$t('deferredState')"
                v-model="project.deferredState.deferred"
                :options="yesNo()"
                :allow-empty="false"
                :show-labels="false"
                deselectLabel
                @select="onChangeProjectDeferredState"
              >
                <template slot="singleLabel" slot-scope="{ option }">
                  <b-badge :variant="yesNoStateReversedColor(option)">
                    {{ $t(option) }}
                  </b-badge>
                </template>
                <template slot="option" slot-scope="{ option }">
                  <b-badge :variant="yesNoStateReversedColor(option)">
                    {{ $t(option) }}
                  </b-badge>
                </template>
              </multiselect>
            </div>

            <template
              v-if="
                project.deferredState.deferred === 'YES' ||
                  project.deferredState.description != '' ||
                  project.deferredState.comment != ''
              "
            >
              <div class="label">{{ $t('reason') }}</div>
              <div>
                {{ $t(project.deferredState.reason) }}
              </div>

              <div class="label">{{ $t('description') }}</div>
              <div>
                <Textarea v-model="project.deferredState.description" rows="3" cols="80" />
              </div>

              <div class="label">{{ $t('comment') }}</div>
              <div>
                <Textarea v-model="project.deferredState.comment" rows="3" cols="80" />
              </div>
            </template>
          </div>
        </div>

        <template
          v-if="
            project.vlink &&
              project.client &&
              (project.client.name === 'Q Power Solution' ||
                project.client.name === 'Q Power Solution Externe Reklamation' ||
                project.client.name === 'Q Cells Test Opolka / Teleson')
          "
        >
          <legend>
            <strong>{{ $t('vlink') }}</strong>
          </legend>

          <div class="form-grid-container-left">
            <div class="label">{{ $t('vlinkConnectedState') }}</div>
            <div>
              <b-badge :variant="yesNoColor(project.vlink.vlinkConnectedState)">
                {{ $t(project.vlink.vlinkConnectedState) }}
              </b-badge>
            </div>
            <div class="label">{{ $t('createdAt') }}</div>
            <div>
              <span v-if="project.vlink.createdAt">
                {{ $datetime(project.vlink.createdAt) }}
              </span>
              <span v-else>{{ $t('not_applicable') }}</span>
            </div>
            <div class="label">{{ $t('lastUpdatedAt') }}</div>
            <div>
              <span v-if="project.vlink.updatedAt">
                {{ $datetime(project.vlink.updatedAt) }}
              </span>
              <span v-else>{{ $t('not_applicable') }}</span>
            </div>
            <div class="label">{{ $t('vlinkProjectNr') }}</div>
            <div>
              <span v-if="project.vlink.projectNr">
                {{ project.vlink.projectNr }}
              </span>
              <span v-else>{{ $t('not_applicable') }}</span>
            </div>
            <div class="label">{{ $t('vlinkProjectId') }}</div>
            <div>
              <span v-if="project.vlink.projectId"> {{ project.vlink.projectId }} </span>
              <span v-else>{{ $t('not_applicable') }}</span>
            </div>
            <div class="label">{{ $t('vlinkToken') }}</div>
            <div>
              <span v-if="project.vlink.token"> {{ project.vlink.token }} </span>
              <span v-else>{{ $t('not_applicable') }}</span>
            </div>
            <div class="label">{{ $t('url') }}</div>
            <div>
              <span v-if="project.vlink.url"> {{ project.vlink.url }} </span>
              <span v-else>{{ $t('not_applicable') }}</span>
            </div>
          </div>
        </template>
      </b-col>
    </b-row>

    <Dialog
      :header="$t('reasonRequired')"
      :visible.sync="displayStateChangeConfirmation"
      :contentStyle="{ overflow: 'visible' }"
      :modal="true"
    >
      <div v-if="project.deferredState">
        <div class="flex-row">
          <div>{{ $t('reason') }}</div>
          <div>
            <Dropdown
              v-model="project.deferredState.reason"
              :options="getEnumValues('DeferredReason')"
              :placeholder="$t('select')"
            >
              <template #option="slotProps">
                <b-badge>
                  {{ $t(slotProps.option) }}
                </b-badge>
              </template>
              <template #value="slotProps">
                <b-badge>
                  {{ $t(slotProps.value) }}
                </b-badge>
              </template>
            </Dropdown>
          </div>
        </div>

        <div class="flex-row">
          <div>{{ $t('description') }}</div>
          <div>
            <Textarea v-model="project.deferredState.description" rows="3" cols="40" />
          </div>
        </div>
      </div>

      <template #footer>
        <Button
          class="btn btn-narrow btn-default ml-2 mb-2"
          @click="onCancelProjectDeferredStateChangeReason"
        >
          {{ $t('cancel') }}
        </Button>
        <Button
          class="btn btn-narrow btn-inverse ml-2 mb-2"
          @click="onSaveProjectDeferredStateChangeReason"
          :disabled="deferredStateOkButtonDisabled"
        >
          {{ $t('ok') }}
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import {
  getYesNoStateColor,
  getDocumentStateColor,
  getProjectStateColor,
  yesNoStateReversedColor,
  projectTypeStateColor,
} from '@/helpers/colors';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import { yesNo } from '@/helpers/enums';
import Dropdown from 'primevue/dropdown';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';

export default {
  components: {
    Multiselect,
    Calendar,
    Dialog,
    Textarea,
    Dropdown,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      displayStateChangeConfirmation: false,
      projectStateBackup: null,
      editor: InlineEditor,
      editorConfig: {},
      filteredUsers: [],
      usersFilter: {
        pagination: {
          skip: 0,
          pageSize: 200,
        },
        sorting: {
          sortField: 'lastname',
          sortOrder: 1,
        },
        filterName: 'project-internal-user-filters',
        filters: {
          userSearch: { value: null },
        },
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient']),
    ...mapGetters(['getEnumValues', 'getClients', 'getEmployers', 'getUsers']),
    employers() {
      // return this.project.client && this.project.client.employers
      //   ? this.project.client.employers
      //   : [];
      return this.getEmployers.filter((employer) => {
        // console.log(employer);
        if (
          employer.client &&
          this.project.client &&
          employer.client.id === this.project.client.id
        ) {
          return employer;
        }
      });
    },
    clients() {
      return this.isClient ? [this.getCurrentUser.client] : this.getClients;
    },
    deliveryAt: {
      get() {
        return this.$calendarFormat(this.project.deliveryAt);
      },
      set(value) {
        this.project.deliveryAt = value;
      },
    },
    /**
     * @todo DRY
     */
    invalidFeedbackNA() {
      return this.$t('preconditionNA');
    },
    deferredStateOkButtonDisabled() {
      return this.project.deferredState && this.project.deferredState.description !== ''
        ? false
        : true;
    },
  },
  methods: {
    ...mapActions([
      'fetchEnumValues',
      'fetchClients',
      'fetchEmployersByClient',
      'fetchEmployers',
      'fetchUsersPaginated',
    ]),
    yesNoColor(state) {
      return getYesNoStateColor(state);
    },
    documentStateColor(state) {
      return getDocumentStateColor(state);
    },
    projectStateColor(state) {
      return getProjectStateColor(state);
    },

    /**
     * if project.deferredState is changed to yes a reason has to be given
     * @param {*} newState
     */
    onChangeProjectDeferredState(newState) {
      if (newState === 'YES') {
        this.displayStateChangeConfirmation = true;
      }
    },

    onCancelProjectDeferredStateChangeReason() {
      // console.log(
      //   'onCancelProjectStateChangeReason()',
      //   this.project.projectState,
      //   this.projectStateBackup
      // );
      this.project.deferredState.deferred = 'NO';
      this.displayStateChangeConfirmation = false;
    },
    onSaveProjectDeferredStateChangeReason() {
      // console.log('onSaveProjectStateChangeReason()', this.project.deferredState);
      this.displayStateChangeConfirmation = false;
    },

    async setup() {
      if (this.isClient) {
        await this.fetchEmployersByClient(this.getCurrentUser.client.id);
      } else {
        await this.fetchClients();
        await this.fetchEmployers();
      }
      await this.fetchEnumValues('ProjectState');
      await this.fetchEnumValues('ProjectTypeState');
      await this.fetchEnumValues('AskEmployerCategoryState');
      await this.fetchEnumValues('DocumentState');
      await this.fetchEnumValues('DeferredReason');
      await this.loadUserData();

      /** init deferredState */
      if (!this.project.deferredState) {
        this.project.deferredState = {
          deferred: 'NO',
          reason: 'MISC',
          description: '',
          comment: '',
        };
      }

      // cache current projectState
      this.projectStateBackup = this.project.projectState;
    },
    /**
     * check requirementsValidState to show mark input fields
     * @todo DRY
     */
    requirementsValidState(field) {
      if (!this.project.netRegistrationRequirements) return false;

      const requirement = this.project.netRegistrationRequirements.find(
        (requirement) => requirement.name === field
      );
      return requirement && requirement.fullfilledState === 'YES' ? true : false;
    },
    /**
     * Fetch user data from graphql
     */
    async loadUserData() {
      await this.fetchUsersPaginated({
        pagination: this.usersFilter.pagination,
        sorting: this.usersFilter.sorting,
        filters: { ...this.usersFilter.filters },
      });
      this.filteredUsers = this.getUsers;
    },
    /**
     * search in dropdown for a user to assign to the project
     */
    async onUserSearch(rawQuery) {
      const query = rawQuery.toLowerCase();
      this.usersFilter.filters.userSearch.value = query;
      await this.loadUserData();
    },
    yesNo,
    yesNoStateReversedColor,
    projectTypeStateColor,
  },

  async mounted() {
    await this.setup();
  },
};
</script>

<style scoped lang="scss">
.form-grid-container-left {
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 1em;
  row-gap: 1em;
}
.form-grid-container-left .label {
  text-align: right;
}
.dense-editor {
  height: auto;
  max-height: 300px;
  overflow-y: auto;
}

.ck-editor__editable_inline {
  min-height: 110px;
  height: auto;
  max-height: 300px;
  overflow-y: auto;
}

.multiselect-users {
  min-width: 230px;
  display: inline-block;
}

::v-deep .multiselect__tags {
  border: 0;
  border-radius: 0;
}

::v-deep .multiselect-users .multiselect__input {
  padding-top: 1em;
}
</style>
